/**
 * AI Recruit - Assessment Support Contact settings
 *
 * Sets the phone number and email address shown to a candidate whose
 * proctored assessment was interrupted (the Assessment Interrupted screen in
 * PublicInterviewPage). That screen is the only place a candidate is told how
 * to follow up, so leaving both blank means an interrupted candidate has no
 * route back to anyone.
 *
 * Stored on the organization's existing `settings` JSONB under
 * "assessment_support" — org-wide, not per job.
 */

import React, { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { PhoneIcon, EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { getSupportContact, updateSupportContact } from '@/services/proctoredInterviewApi'

export default function AssessmentSupportPage() {
  const queryClient = useQueryClient()
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')

  const { data: contact, isLoading } = useQuery({
    queryKey: ['assessment-support-contact'],
    queryFn: getSupportContact,
  })

  // Seeds the inputs once the saved values arrive. Keyed on `contact` rather
  // than running on mount, because the query resolves after the first render.
  useEffect(() => {
    if (!contact) return
    setPhone(contact.phone || '')
    setEmail(contact.email || '')
  }, [contact])

  const saveMut = useMutation({
    mutationFn: () => updateSupportContact({ phone: phone.trim(), email: email.trim() }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['assessment-support-contact'], saved)
      toast.success('Support contact saved.')
    },
    onError: () => {
      // api.ts's response interceptor already toasts 403s.
      toast.error('Could not save the support contact. Please try again.')
    },
  })

  const bothBlank = !phone.trim() && !email.trim()
  const emailLooksValid = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Assessment Support Contact</h1>
      <p className="text-sm text-gray-500 mb-6">
        Shown to candidates whose proctored assessment is interrupted, so they know how to reach
        you about getting a new link. Both fields are optional — whichever you leave blank is
        simply omitted from that screen.
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div>
          <label htmlFor="support-phone" className="block text-sm font-medium text-gray-900 mb-1.5">
            Phone number
          </label>
          <div className="relative">
            <PhoneIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="support-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 0100"
              maxLength={50}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="support-email" className="block text-sm font-medium text-gray-900 mb-1.5">
            Email address
          </label>
          <div className="relative">
            <EnvelopeIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="support-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="talent@yourcompany.com"
              maxLength={255}
              className={`w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 ${
                emailLooksValid
                  ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  : 'border-red-300 focus:ring-red-500 focus:border-red-500'
              }`}
            />
          </div>
          {!emailLooksValid && (
            <p className="text-xs text-red-500 mt-1">That does not look like a valid email address.</p>
          )}
        </div>

        {bothBlank && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              With both fields empty, a candidate whose assessment is interrupted is told it ended
              but is given no way to reach anyone about it.
            </p>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !emailLooksValid}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            {saveMut.isPending ? <LoadingSpinner size="sm" /> : null}
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
